/**
 * web端模拟客户端的bridge方式
 * 用于文件消费页面调用方式的统一
 */

import type {
  PPGetAppMetaDataResponse,
  PPGetUserInfoParams,
  PPReportParams,
  PPRequestParams,
  PPRequestResponse,
} from '@pikpak/pp-js-bridge'
import { useUserInfo } from '~/store/userInfo'
import useFetch from '~~/composables/useXFetch'
import { getLocale } from '~~/plugins/i18n'

type TParams = Record<string, unknown>
type TResponseStatus = { code: number; description: string } | null
type TResponseData<T = TParams> = { result: Partial<T> } | null
type TCallBack<T = TParams> = (status: TResponseStatus, data?: TResponseData<T>) => void

function execCallback<T = TParams>(status: TResponseStatus, data: Partial<T> | null, callback?: TCallBack) {
  callback?.(status, data ? { result: data } : data)
}

/** 获取信息 */
const ppGetAppMetaData = (params: TParams, callback?: TCallBack<PPGetAppMetaDataResponse>) => {
  execCallback<PPGetAppMetaDataResponse>(
    null,
    {
      language: getLocale(),
      uiMode: 'white',
    },
    callback,
  )
}

/** 接口请求 */
const ppRequest = (params: PPRequestParams, callback?: TCallBack<PPRequestResponse>) => {
  if (!params || !params.url) {
    return execCallback({ code: -1, description: 'Invalid request' }, null, callback)
  }
  const { url, ...options } = params
  if (options.withCredential) {
    ;(options as TParams).withCredentials = true
  }
  useFetch(url, options).then(({ data, error }) => {
    // console.log(data.value, error.value)
    if (error.value) {
      execCallback<PPRequestResponse>(
        null,
        {
          errorKey: error.value.error_code,
          errorDescription: error.value.error_description,
        },
        callback,
      )
    } else {
      try {
        const response = JSON.parse(JSON.stringify(data.value || {}))
        execCallback<PPRequestResponse>(null, { response }, callback)
      } catch (err) {
        execCallback<PPRequestResponse>(
          null,
          {
            errorDescription: (err as Error).message,
          },
          callback,
        )
      }
    }
  })
}

/** 页面关闭, 暂时无实际作用，主要为了web不报错 */
const ppFinishSelf = (params: TParams, callback?: TCallBack) => {
  execCallback(null, null, callback)
}

const ppReport = (params: PPReportParams, callback?: TCallBack) => {
  window.stat(params.hubbleEventId, params.hubbleAttribute, params.hubbleExData)
  execCallback(null, null, callback)
}

// 获取用户信息
const ppGetUserInfo = (params: TParams, callback?: TCallBack<PPGetUserInfoParams>) => {
  const userInfo = useUserInfo()
  execCallback<PPGetUserInfoParams>(
    null,
    {
      userID: userInfo.userId,
      isVip: !!userInfo.premium,
    },
    callback,
  )
}

const bridgeFunc = {
  ppGetAppMetaData,
  ppRequest,
  ppFinishSelf,
  ppReport,
  ppGetUserInfo,
}
type TBridgeFunc = typeof bridgeFunc
type TBridgeNames = keyof TBridgeFunc
type TBridgeParams<T extends TBridgeNames> = Parameters<TBridgeFunc[T]>[0]

export const injectJsBridge = () => {
  window.PikPakJSBridge = {
    sendMessage<T extends TBridgeNames>(fnName: T, params: TBridgeParams<T>, callback?: TCallBack) {
      // console.log(fnName, params)
      if (bridgeFunc[fnName]) {
        // @ts-ignore
        bridgeFunc[fnName](params, callback)
      } else {
        execCallback({ code: -1, description: 'not implement this method.' }, null, callback)
      }
    },
  }
}
